import { useEffect, useRef, useState } from "react";
import {
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";

import SortArrowIcon from "../../Icon-SVG/SortArrowIcon";
import ReactTableWrapperRow from "./Row/ReactTableWrapperRow";
import PaginationLeftIcon from "../../Icon-SVG/PaginationLeftIcon";
import PaginationRightIcon from "../../Icon-SVG/PaginationRightIcon";
import ExportReactCSV from "../../../utils/ExportCSV";
import ExportXLSX from "../../../utils/ExportXLSX";
import ExpandButton from "../Table/Components/ExpandButton";
import TableSpinnerIcon from "../../Icon-SVG/TableSpinnerIcon";
import CustomSelect from "../CustomSelect/CustomSelect";
import PageSizeSelect, { usePageSize } from "../PageSizeSelect/PageSizeSelect";
import Overlay from "../../UI/Overlay/Overlay";
import Backdrop from "../../UI/Backdrop/Backdrop";

import "./styles.scss";

export const FILTER_BY_TIME = [
  {
    id: "1-hour",
    value: "1 hour",
  },
  { id: "3-hour", value: "3 hours" },
  { id: "1-day", value: "1 day" },
  { id: "3-day", value: "3 days" },
  { id: "7-day", value: "7 days" },
  { id: "30-day", value: "30 days" },
  { id: "all-all", value: "Show all" },
];

const ReactTableWrapper = ({
  columns,
  data,
  isDataLoading,
  tableName,
  tableClassName,
  headerClassName,
  paginationClassName,
  styles = {},
  customPageSize,
  paginationButtons = true,
  pagination = {},
  refreshConfig = {},
  filterByTime = {},
  exportFile = true,
  expandConfig = {},
  minHeightRow = "55",
  rowOnClickHandler,
  getRowProps,
  onFilterChange = null,
  onSelectedRowsChange = null,
}) => {
  const {
    show: filterByTimeEnabled = false,
    label: filterByTimeLabel,
    data: filterByTimeData,
    defaultIndex: filterByTimeDefaultIndex,
    selectedTimeFilter,
    setSelectedTimeFilter,
  } = filterByTime;

  const {
    manualPagination,
    totalPageCount,
    serverSidePaginationNumber,
    setServerSidePaginationNumber,
    onPageSizeChange,
  } = pagination;

  const tableStartRef = useRef(null);
  const [pageSize, setPageSizePreference] = usePageSize(tableName, customPageSize);

  const [refreshClicked, setRefreshClicked] = useState(false);
  const [internalPageNumber, setInternalPageNumber] = useState(1);
  const pageNumber = setServerSidePaginationNumber
    ? serverSidePaginationNumber
    : internalPageNumber;
  const setPageNumber = setServerSidePaginationNumber
    ? setServerSidePaginationNumber
    : setInternalPageNumber;

  // useEffect(() => {
  //   localStorage.setItem("tableTimeFilter", JSON.stringify(filterByTime.selectedTimeFilter));
  // }, [filterByTime.selectedTimeFilter]);

  useEffect(() => {
    const storedData = localStorage.getItem(tableName);
    if (storedData && filterByTimeEnabled) {
      const parsedData = JSON.parse(storedData);
      if (JSON.stringify(selectedTimeFilter) !== JSON.stringify(parsedData)) {
        setSelectedTimeFilter(parsedData);
      }
    }

    if (!storedData && selectedTimeFilter && filterByTimeEnabled) {
      localStorage.setItem(tableName, JSON.stringify(selectedTimeFilter));
    }
  }, [filterByTimeEnabled, selectedTimeFilter, setSelectedTimeFilter, tableName]);

  const onSelectFilterItem = (selectedTimeFilter) => {
    // setRefreshButton(true);
    localStorage.setItem(tableName, JSON.stringify(selectedTimeFilter));
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    nextPage,
    previousPage,
    pageOptions,
    gotoPage,
    setPageSize,
    visibleColumns,
    toggleAllRowsExpanded,
    selectedFlatRows,
    toggleAllRowsSelected,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize,
      },
      autoResetPage: false,
      autoResetFilters: false,
      manualPagination,
      manualFilters: manualPagination, // if there is server side pagination there have to be server side filtering
      pageCount: manualPagination ? totalPageCount : -1,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      expandConfig?.use &&
        hooks.visibleColumns.push((columns) => [
          {
            id: "expander",
            Cell: expandConfig?.Button ? expandConfig.Button : ExpandButton,
            headerClassName: expandConfig?.buttonHeaderClassName
              ? expandConfig.buttonHeaderClassName
              : null,
            cellClassName: expandConfig?.buttonCellClassName
              ? expandConfig?.buttonCellClassName
              : null,
          },
          ...columns,
        ]);
    },
    // (hooks) => {
    //   leftAction && !checkbox
    //     ? hooks.visibleColumns.push((columns) => [
    //         {
    //           id: "expander",
    //           Cell: ExpandButton,
    //         },
    //         ...columns,
    //       ])
    //     : checkbox &&
    //       hooks.visibleColumns.push((columns) => [
    //         {
    //           id: "selection",
    //           Header: ({ getToggleAllRowsSelectedProps }) => (
    //             <CheckboxTable id={new Date()} {...getToggleAllRowsSelectedProps()} />
    //           ),
    //           Cell: ({ row }) => <CheckboxTable id={row.id} {...row.getToggleRowSelectedProps()} />,
    //         },
    //         ...columns,
    //       ]);
    // },
  );

  const { pageIndex, filters, selectedRowIds } = state;

  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
    } else {
      // A new search can shrink the result below the current page, leaving it empty.
      gotoPage(0);
      setPageNumber(1);
    }
    if (onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters]);

  // Keyed on selectedRowIds, not selectedFlatRows: the latter is a fresh array on
  // every render and would loop back through the parent's state update forever.
  useEffect(() => {
    if (onSelectedRowsChange) {
      onSelectedRowsChange(selectedFlatRows.map((row) => row.original));
    }
  }, [selectedRowIds]);

  // Selection is keyed by row index, so a new data set would silently re-point it
  // at different records. Cleared explicitly rather than leaning on
  // autoResetSelectedRows, because a stale selection here means acting on the
  // wrong agents.
  useEffect(() => {
    if (onSelectedRowsChange) {
      toggleAllRowsSelected(false);
    }
  }, [data]);

  useEffect(() => {
    if (serverSidePaginationNumber && setServerSidePaginationNumber) {
      gotoPage(serverSidePaginationNumber - 1);
    }
  }, [serverSidePaginationNumber]);

  useEffect(() => {
    if (!isDataLoading) {
      setRefreshClicked(false);
    }
  }, [isDataLoading]);

  const refreshHandler = () => {
    setRefreshClicked(true);
    refreshConfig.onClickHandler();
  };

  const pageSwitchHandler = (numPage) => {
    if (numPage >= 0) {
      toggleAllRowsExpanded(false);
      if (tableStartRef && tableStartRef.current) {
        tableStartRef.current.scrollIntoView();
      }
      setPageNumber(numPage + 1);
      gotoPage(numPage);
    } else {
      setPageNumber(0);
      gotoPage(0);
    }
  };

  const inputPageSwitchHandler = (numPage) => {
    if (numPage >= 0) {
      toggleAllRowsExpanded(false);
      if (tableStartRef && tableStartRef.current) {
        tableStartRef.current.scrollIntoView();
      }
      setPageNumber(numPage + 1);
      gotoPage(numPage);
    } else {
      setPageNumber(0);
      gotoPage(0);
    }
  };

  // Server-paged tables refetch through onPageSizeChange, starting from page 1
  const pageSizeChangeHandler = (size) => {
    toggleAllRowsExpanded(false);
    setPageSizePreference(size);
    gotoPage(0);
    setPageNumber(1);
    if (onPageSizeChange) {
      onPageSizeChange(size);
    }
  };

  useEffect(() => {
    setPageSize(pageSize);
  }, [pageSize, setPageSize]);

  const previousPageHandler = () => {
    if (leftIconDisabled) return;

    setPageNumber((prevState) => prevState - 1);
    previousPage();
  };

  const nextPageHandler = () => {
    if (rightIconDisabled) return;

    setPageNumber((prevState) => prevState + 1);
    nextPage();
  };

  let leftIconDisabled = false;
  let rightIconDisabled = false;

  leftIconDisabled = pageIndex === 0;
  rightIconDisabled = pageIndex === pageOptions.length - 1;

  // The selection column has no accessor and renders a checkbox as its header,
  // which has nothing to contribute to an exported file.
  const exportColumns = columns.filter((column) => column.id !== "selection");

  return (
    <>
      <div
        className="custom-table custom-table__main-table"
        style={{ minHeight: `${minHeightRow * customPageSize + 35}px` }}
        ref={tableStartRef}>
        <div className={`custom-table-button-block custom-table-button-block--${tableName}`}>
          {exportFile && (
            <>
              <ExportXLSX
                csvData={selectedFlatRows.length ? selectedFlatRows : data}
                fileName={tableName}
                selected={selectedFlatRows.length}
                columns={exportColumns}
              />
              <ExportReactCSV
                csvData={selectedFlatRows.length ? selectedFlatRows : data}
                selected={selectedFlatRows.length}
                tableName={tableName}
                fileName={`${tableName}.csv`}
                headers={exportColumns.map((column) => ({
                  label: column.Header,
                  key: column.accessor,
                }))}
              />
            </>
          )}
          {refreshConfig?.show && (
            <button
              style={{ backgroundColor: "transparent", border: "none" }}
              onClick={refreshHandler}>
              <TableSpinnerIcon className="spinner" spin={isDataLoading && refreshClicked} />
            </button>
          )}
          {filterByTimeEnabled && (
            <div className="custom-table-button-block-filter">
              <p>{filterByTimeLabel ? filterByTimeLabel : "Filter by last:"} </p>
              <CustomSelect
                data={filterByTimeData ? filterByTimeData : FILTER_BY_TIME}
                select={selectedTimeFilter}
                setSelected={setSelectedTimeFilter}
                width="120px"
                height="26px"
                defaultIndex={filterByTimeDefaultIndex ? filterByTimeDefaultIndex : 0}
                onSelectItem={onSelectFilterItem}
              />
            </div>
          )}
        </div>
        <table
          //   style={tableBorder ? { border: "none" } : {}}
          id={tableName}
          {...getTableProps()}
          className={`${tableClassName}`}
          style={styles.table}>
          <thead className={headerClassName} style={styles.header}>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} style={styles.headerRow}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps}
                    key={column.id}
                    className={column.headerClassName ? column.headerClassName : null}
                    style={styles.headerCell}>
                    <div className="th-row">
                      <>{column.Filter && column.canFilter ? column.render("Filter") : null}</>
                      {column.sort ? (
                        <div
                          className="th-sort"
                          {...column.getHeaderProps(column.getSortByToggleProps())}>
                          <p>{column.render("Header")}</p>
                          {column.Header && (
                            <span className="m-l-5">
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <SortArrowIcon desc="var(--sort-color-asc)" />
                                ) : (
                                  <SortArrowIcon asc="var(--sort-color-desc)" />
                                )
                              ) : column.id !== "action" ? (
                                <SortArrowIcon />
                              ) : (
                                ""
                              )}
                            </span>
                          )}
                        </div>
                      ) : (
                        <p>{column.render("Header")}</p>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} style={{ ...styles.body }}>
            {page.map((row, index) => {
              prepareRow(row);
              const rowProps = getRowProps ? getRowProps(row, index) : {};
              return (
                <ReactTableWrapperRow
                  // arrow={leftAction}
                  key={index}
                  row={row}
                  visibleColumns={visibleColumns}
                  expandConfig={expandConfig}
                  minHeight={minHeightRow}
                  onClickHandler={rowOnClickHandler}
                  rowProps={rowProps}
                  tableName={tableName}
                />
              );
            })}
          </tbody>
        </table>
        {refreshClicked && <Backdrop absolute={true} show={isDataLoading} />}
      </div>
      {paginationButtons && (
        <div
          className={`custom-table__footer-pagination ${paginationClassName}`}
          style={{
            height: "38px",
            ...styles.pagination,
          }}>
          <PageSizeSelect
            value={pageSize}
            defaultSize={customPageSize}
            onChange={pageSizeChangeHandler}
          />
          <div
            className="custom-table__footer-pagination-arrow"
            onClick={previousPageHandler}
            disabled={leftIconDisabled}>
            <PaginationLeftIcon
              color={
                leftIconDisabled
                  ? "var(--dropped--file-pagination-arrow-color)"
                  : "var(--dropped--file-pagination-arrow-active-color)"
              }
            />
          </div>
          {pageOptions.map((number) =>
            number < 1 || pageOptions.length - 2 < number ? (
              <p
                className={`custom-table__footer-pagination__number${
                  pageIndex === number ? "__active" : ""
                }`}
                key={number}
                onClick={() => pageSwitchHandler(number)}>
                {number + 1}
              </p>
            ) : (pageIndex < 3 && number < 5) ||
              (pageOptions.length - 4 < pageIndex && pageOptions.length - 6 < number) ? (
              <p
                className={`custom-table__footer-pagination__number${
                  pageIndex === number ? "__active" : ""
                }`}
                key={number}
                onClick={() => pageSwitchHandler(number)}>
                {number + 1}
              </p>
            ) : (pageIndex < 3 && number === 5) ||
              (pageOptions.length - 4 < pageIndex && pageOptions.length - 6 === number) ? (
              <p className={`custom-table__footer-pagination__number`} key={number}>
                ...
              </p>
            ) : pageIndex - 2 < number && pageIndex + 2 > number ? (
              <p
                className={`custom-table__footer-pagination__number${
                  pageIndex === number ? "__active" : ""
                }`}
                key={number}
                onClick={() => pageSwitchHandler(number)}>
                {number + 1}
              </p>
            ) : pageIndex - 2 === number || pageIndex + 2 === number ? (
              <p className={`custom-table__footer-pagination__number`} key={number}>
                ....
              </p>
            ) : null,
          )}
          <div
            className="custom-table__footer-pagination-arrow"
            onClick={nextPageHandler}
            disabled={rightIconDisabled}>
            <PaginationRightIcon
              color={
                rightIconDisabled
                  ? "var(--dropped--file-pagination-arrow-color)"
                  : "var(--dropped--file-pagination-arrow-active-color)"
              }
            />
          </div>
          <div className="custom-table__footer-pagination__number-box">
            <input
              className="border"
              type="text"
              value={
                pageNumber === 0
                  ? ""
                  : pageNumber > pageOptions.length
                    ? pageOptions.length
                    : pageNumber
              }
              onChange={(e) => inputPageSwitchHandler(Number(e.target.value) - 1)}
            />
            <span> | {pageOptions.length}</span>
          </div>
          <Overlay show={isDataLoading} />
        </div>
      )}
    </>
  );
};

export default ReactTableWrapper;
